import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { useApiQuery } from '../useApiQuery';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { estimateKeys, expenseKeys, jobKeys } from '@/lib/queryKeys';
import { normalizeJob } from '@/lib/normalizeJob';
import { createListQueryHook } from '@/hooks/lib/createListQueryHook';
import type {
  CreateJobPaymentLinkDto,
  Job,
  JobRelatedResponse,
  JobListParams,
  JobListResponse,
  JobPaymentLinkResult,
  JobPaymentListResponse,
  JobPaymentSummary,
  RecordJobPaymentDto,
  CreateJobApprovalEstimateResult,
} from '@/types/job';

/**
 * Fetches the paginated list of jobs from the operations microservice.
 */
export const useJobs = createListQueryHook<JobListParams, JobListResponse>({
  queryKey: (params) => jobKeys.list(params),
  endpoint: () => jobEndpoints.list(),
  itemsKey: 'data',
  normalizeItem: (raw) => normalizeJob(raw) as Job,
});

export function useJobPayments(id: string, limit = 10, { enabled = true } = {}) {
  return useApiQuery<JobPaymentListResponse>({
    // limit is part of the key so the 10-row (one-off) and full (recurring) variants
    // don't collide; jobKeys.payments(id) stays the invalidation prefix.
    queryKey: [...jobKeys.payments(id), limit],
    queryFn: () =>
      apiGet<JobPaymentListResponse>(operationsClient, jobEndpoints.payments(id), {
        params: { page: 1, limit },
      }),
    enabled: Boolean(id) && enabled,
    showErrorToast: false,
  });
}

export function useAddJobItem(id: string) {
  const queryClient = useQueryClient();

  return useApiMutation<Job, Record<string, unknown>>({
    mutationFn: (dto) => apiPost<Job>(operationsClient, jobEndpoints.addItem(id), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: expenseKeys.profitability(id) });
      toast.success('Job item added');
    },
  });
}

export function useUpdateJobItem(id: string) {
  const queryClient = useQueryClient();

  return useApiMutation<Job, { itemId: string; dto: Record<string, unknown> }>({
    mutationFn: ({ itemId, dto }) =>
      apiPut<Job>(operationsClient, jobEndpoints.updateItem(id, itemId), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: expenseKeys.profitability(id) });
      toast.success('Job item updated');
    },
  });
}

export function useDeleteJobItem(id: string) {
  const queryClient = useQueryClient();

  return useApiMutation<Job, string>({
    mutationFn: (itemId) => apiDelete<Job>(operationsClient, jobEndpoints.deleteItem(id, itemId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: expenseKeys.profitability(id) });
      toast.success('Job item removed');
    },
  });
}

export function useCreateJobPaymentLink(id: string) {
  const queryClient = useQueryClient();

  return useApiMutation<JobPaymentLinkResult, CreateJobPaymentLinkDto>({
    mutationFn: (dto) =>
      apiPost<JobPaymentLinkResult>(operationsClient, jobEndpoints.paymentRequestLink(id), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.payments(id) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast.success('Job payment link created');
    },
  });
}

/** Everything a job's Payments section and its money totals read. */
export function invalidateJobPaymentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
) {
  void queryClient.invalidateQueries({ queryKey: jobKeys.payments(id) });
  void queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
  void queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(id) });
  void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
}

/**
 * Refreshes a job's payments after money reached it by another route — e.g.
 * a new card charged through the contact-collect endpoint with `jobId`, whose
 * own hook only refreshes contact queries.
 */
export function useInvalidateJobPayments(id: string) {
  const queryClient = useQueryClient();
  return useCallback(() => invalidateJobPaymentQueries(queryClient, id), [queryClient, id]);
}

/**
 * Record a manual/offline payment (cash, cheque, card taken elsewhere) directly
 * against a job, with no invoice. The job page previously offered only
 * "Create payment link", so money collected in the field had nowhere to go
 * until someone invoiced the job.
 */
export function useRecordJobPayment(id: string) {
  const queryClient = useQueryClient();

  return useApiMutation<JobPaymentSummary, RecordJobPaymentDto>({
    mutationFn: (dto) => apiPost<JobPaymentSummary>(operationsClient, jobEndpoints.payments(id), dto),
    onSuccess: () => {
      invalidateJobPaymentQueries(queryClient, id);
      toast.success('Payment recorded');
    },
  });
}

export function useJobRelated(id: string, options?: { enabled?: boolean }) {
  return useApiQuery<JobRelatedResponse>({
    queryKey: jobKeys.related(id),
    queryFn: () => apiGet<JobRelatedResponse>(operationsClient, jobEndpoints.related(id)),
    enabled: (options?.enabled ?? true) && Boolean(id),
    showErrorToast: false,
  });
}

export function useCreateJobApprovalEstimate(id: string) {
  const queryClient = useQueryClient();

  return useApiMutation<CreateJobApprovalEstimateResult, void>({
    mutationFn: () =>
      apiPost<CreateJobApprovalEstimateResult>(
        operationsClient,
        jobEndpoints.createApprovalEstimate(id),
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      toast.success('Approval estimate created');
    },
  });
}

// NOT-560 — create an estimate *on* a SERVICE_CALL job. Unlike the approval
// flow this is a fan-out (one call → many estimates via sourceJobId) and does
// not claim the job's estimateId, so it can be called repeatedly on one call.
export function useCreateServiceCallEstimate(id: string) {
  const queryClient = useQueryClient();

  return useApiMutation<CreateJobApprovalEstimateResult, void>({
    mutationFn: () =>
      apiPost<CreateJobApprovalEstimateResult>(
        operationsClient,
        jobEndpoints.createServiceCallEstimate(id),
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      toast.success('Estimate created');
    },
  });
}
